import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";

const formatPercent = (value) =>
  value == null || Number.isNaN(Number(value)) ? "" : `${Number(value).toFixed(1)}%`;

// Values arrive on a 0-100 scale; show them as percentages on the axis and tooltip
const PERCENT_OPTIONS = {
  scales: {
    y: {
      min: 0,
      max: 100,
      ticks: { stepSize: 20, callback: (value) => `${value}%` },
    },
  },
  plugins: {
    tooltip: {
      callbacks: {
        label: (ctx) => `${ctx.dataset.label ? `${ctx.dataset.label}: ` : ""}${formatPercent(ctx.parsed.y)}`,
      },
    },
  },
};

function JunctionsChartsPanel({ chartsData, loading = false, metricLabel = "", percent = false }) {
  const { t } = useTranslation();
  const charts = chartsData?.data ?? [];

  return (
    <Card sx={{ p: 2, mt: 2 }}>
      <MDTypography variant="h6">
        {metricLabel ? t("junctions.charts.titleWithMetric", { metric: metricLabel }) : t("junctions.charts.title")}
      </MDTypography>
      <Divider />
      {loading ? (
        <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={2}>
          <CircularProgress color="info" />
          <MDTypography variant="button" color="text">
            {t("junctions.charts.loading")}
          </MDTypography>
        </MDBox>
      ) : (
        <Grid container spacing={2} mt={1}>
          {charts.map((chartObj, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <Grid item xs={12} md={6} key={idx}>
              <VerticalBarChart
                title={chartObj.datasets?.[0]?.label || t("junctions.charts.fallbackTitle", { index: idx + 1 })}
                chart={chartObj}
                height="18rem"
                options={percent ? PERCENT_OPTIONS : undefined}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Card>
  );
}

JunctionsChartsPanel.propTypes = {
  loading: PropTypes.bool,
  chartsData: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        labels: PropTypes.arrayOf(PropTypes.string),
        datasets: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string,
            data: PropTypes.arrayOf(PropTypes.number),
            color: PropTypes.string,
          })
        ),
      })
    ),
  }).isRequired,
  metricLabel: PropTypes.string,
  percent: PropTypes.bool,
};

export default JunctionsChartsPanel;
